import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

const CALENDAR_OPTIONS = [
  { id: 'apple', label: 'Apple Calendar' },
  { id: 'google', label: 'Google' },
  { id: 'outlook', label: 'Outlook' },
  { id: 'outlookcom', label: 'Outlook.com' },
  { id: 'yahoo', label: 'Yahoo' },
];

export function formatCalendarTime(date) {
  return moment.utc(date).format('YYYYMMDDTHHmmssZ').replace('+00:00', 'Z');
}

function calculateDuration(startTime, endTime) {
  const difference = moment.utc(endTime).diff(moment.utc(startTime));
  const duration = moment.duration(difference);

  return Math.floor(duration.asHours()) + moment.utc(difference).format(':mm');
}

export function buildCalendarUrl(event, type) {
  const title = event.title || '';
  const description = event.description || '';
  const location = event.location || '';

  switch (type) {
    case 'google': {
      let calendarUrl = 'https://calendar.google.com/calendar/render';
      calendarUrl += '?action=TEMPLATE';
      calendarUrl += `&dates=${formatCalendarTime(event.startTime)}`;
      calendarUrl += `/${formatCalendarTime(event.endTime)}`;
      calendarUrl += `&location=${encodeURIComponent(location)}`;
      calendarUrl += `&text=${encodeURIComponent(title)}`;
      calendarUrl += `&details=${encodeURIComponent(description)}`;
      return calendarUrl;
    }

    case 'yahoo': {
      const duration = calculateDuration(event.startTime, event.endTime);
      let calendarUrl = 'https://calendar.yahoo.com/?v=60&view=d&type=20';
      calendarUrl += `&title=${encodeURIComponent(title)}`;
      calendarUrl += `&st=${formatCalendarTime(event.startTime)}`;
      calendarUrl += `&dur=${duration}`;
      calendarUrl += `&desc=${encodeURIComponent(description)}`;
      calendarUrl += `&in_loc=${encodeURIComponent(location)}`;
      return calendarUrl;
    }

    case 'outlookcom': {
      let calendarUrl = 'https://outlook.live.com/owa/?rru=addevent';
      calendarUrl += `&startdt=${formatCalendarTime(event.startTime)}`;
      calendarUrl += `&enddt=${formatCalendarTime(event.endTime)}`;
      calendarUrl += `&subject=${encodeURIComponent(title)}`;
      calendarUrl += `&location=${encodeURIComponent(location)}`;
      calendarUrl += `&body=${encodeURIComponent(description)}`;
      calendarUrl += '&allday=false';
      calendarUrl += `&uid=${Date.now()}`;
      calendarUrl += '&path=/calendar/view/Month';
      return calendarUrl;
    }

    default:
      return [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'BEGIN:VEVENT',
        `URL:${typeof document !== 'undefined' ? document.URL : ''}`,
        `DTSTART:${formatCalendarTime(event.startTime)}`,
        `DTEND:${formatCalendarTime(event.endTime)}`,
        `SUMMARY:${title}`,
        `DESCRIPTION:${description}`,
        `LOCATION:${location}`,
        'END:VEVENT',
        'END:VCALENDAR',
      ].join('\n');
  }
}

function downloadIcs(icsContent) {
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.setAttribute('download', 'event.ics');
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(link.href);
}

export default function AddToCalendarButton({ event }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const onDocClick = (e) => {
      if (rootRef.current?.contains(e.target)) {
        return;
      }

      setOpen(false);
    };

    const id = setTimeout(() => document.addEventListener('click', onDocClick), 0);

    return () => {
      clearTimeout(id);
      document.removeEventListener('click', onDocClick);
    };
  }, [open]);

  const onToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen((prev) => !prev);
  };

  const onOptionClick = (e, type) => {
    e.preventDefault();
    const url = buildCalendarUrl(event, type);

    if (url.startsWith('BEGIN:VCALENDAR')) {
      downloadIcs(url);
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }

    setOpen(false);
  };

  const buttonClass = open
    ? 'react-add-to-calendar__button react-add-to-calendar__button--light'
    : 'react-add-to-calendar__button';

  return (
    <div className="react-add-to-calendar" ref={rootRef}>
      <div className="react-add-to-calendar__wrapper">
        <a className={buttonClass} href="#" onClick={onToggle}>
          <span>Add to My Calendar</span>
        </a>
      </div>
      {open && (
        <div className="react-add-to-calendar__dropdown">
          <ul>
            {CALENDAR_OPTIONS.map((option) => (
              <li key={option.id}>
                <a
                  className={`${option.id}-link`}
                  href="#"
                  onClick={(e) => onOptionClick(e, option.id)}
                >
                  {option.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

AddToCalendarButton.propTypes = {
  event: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    location: PropTypes.string,
    startTime: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]).isRequired,
    endTime: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]).isRequired,
  }).isRequired,
};
